/**
 * Keep factory 品题 list / dialog stems as pure main question —
 * drop glued sub-question or solution narrations after the equation.
 */
const SOLUTION_NARRATION_RE =
  /[（(]\s*\d+\s*[）)]\s*(观察|计算|利用|应用|拆分|分解|结合|分配|展开|先算|再算|最后|得出|根据|将|化简|寻找|提取|写成|公因|结果)/u;

/** Solution / method lines often land in subQuestions without a (n) prefix. */
const NARRATION_LEAD_RE =
  /^(?:[（(]\s*\d+\s*[）)]\s*)?(观察|利用|应用|拆分|分解|结合|分配|展开|先算|再算|最后|得出|根据|将|化简|寻找|提取|写成|公因|结果)/u;

const INDEX_ONLY_RE = /^【\s*\d+\s*】\s*[:：]?$/u;

const MATHISH_RE = /[=＝]|[×÷+\-−*/]|\$|\\times|\\div|\d\s*[×x*×]/;

export function isThinFactoryQuestionStem(text: string): boolean {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return true;
  return INDEX_ONLY_RE.test(s);
}

function indexPrefix(text: string): string {
  const m = String(text || '').match(/^【\s*\d+\s*】/);
  return m?.[0] || '';
}

function looksLikeEquationStem(text: string): boolean {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return false;
  if (MATHISH_RE.test(s)) return true;
  // Textbook stems like「计算：12345679 × 9 =」
  if (/^(计算|求|列式|填空|解)[:：]/.test(s) && /\d/.test(s)) return true;
  return false;
}

function looksLikeSolutionNarration(text: string): boolean {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return false;
  // Method prose without an equation / trailing "=" is not a stem.
  if (!NARRATION_LEAD_RE.test(s)) return false;
  if (looksLikeEquationStem(s) && /[=＝]/.test(s)) return false;
  return true;
}

/** First non-empty line; if it's only 【n】, keep joining until substance appears. */
function pickStemLead(raw: string): string {
  const lines = String(raw || '')
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!lines.length) return '';
  let lead = lines[0]!;
  let i = 1;
  while (isThinFactoryQuestionStem(lead) && i < lines.length) {
    // Prefer joining an equation line; skip pure method narration lines.
    const next = lines[i]!;
    if (looksLikeSolutionNarration(next) && !looksLikeEquationStem(next)) {
      i += 1;
      continue;
    }
    lead = `${lead} ${next}`.trim();
    i += 1;
  }
  return lead.replace(/\s+/g, ' ').trim();
}

function pickEquationSub(subs: string[]): string | null {
  for (const sub of subs) {
    if (looksLikeEquationStem(sub) && !looksLikeSolutionNarration(sub)) return sub;
  }
  return null;
}

/**
 * Strip glued `(n)…` tails when structured subs exist — but never drop the only
 * equation body that lives inside `（1）算式`.
 */
function stripTrailingNumberedBlocks(text: string, hasSubs: boolean): string {
  if (!hasSubs) return text;
  const before = text;
  const stripped = text.replace(/\s*[（(]\s*\d+\s*[）)][\s\S]*$/u, '').trim();
  if (!isThinFactoryQuestionStem(stripped) && !looksLikeSolutionNarration(stripped)) {
    return stripped;
  }

  const m = before.match(/^([\s\S]*?)[（(]\s*\d+\s*[）)]\s*([\s\S]+)$/u);
  if (!m) return before;
  const head = m[1]!.replace(/\s+/g, ' ').trim();
  let body = m[2]!.replace(/\s+/g, ' ').trim();
  // Keep only the first numbered segment's body.
  body = body.split(/[（(]\s*\d+\s*[）)]/u)[0]!.trim();
  const narr = body.search(SOLUTION_NARRATION_RE);
  if (narr > 0) body = body.slice(0, narr).replace(/\s+$/, '');
  if (looksLikeSolutionNarration(body) && !looksLikeEquationStem(body)) {
    return isThinFactoryQuestionStem(head) ? before : head;
  }
  if (looksLikeEquationStem(body) || (!isThinFactoryQuestionStem(body) && body)) {
    return [head || indexPrefix(before), body].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  }
  return before;
}

function withEquationFallback(stem: string, subs: string[], rawCollapsed: string): string {
  const idx = indexPrefix(stem) || indexPrefix(rawCollapsed);

  if (!isThinFactoryQuestionStem(stem) && !looksLikeSolutionNarration(stem)) return stem;

  const bestSub = pickEquationSub(subs);
  if (bestSub) {
    return [idx, bestSub].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  }

  if (
    !isThinFactoryQuestionStem(rawCollapsed)
    && looksLikeEquationStem(rawCollapsed)
    && !looksLikeSolutionNarration(rawCollapsed)
  ) {
    return rawCollapsed;
  }

  // Recover equation body from raw `【n】（1）算式` even when stem was thinned.
  const recovered = stripTrailingNumberedBlocks(rawCollapsed, true);
  if (!isThinFactoryQuestionStem(recovered) && recovered !== rawCollapsed) {
    return recovered;
  }
  if (
    !isThinFactoryQuestionStem(recovered)
    && looksLikeEquationStem(recovered)
  ) {
    return recovered;
  }

  if (isThinFactoryQuestionStem(stem) || looksLikeSolutionNarration(stem)) {
    return idx || stem;
  }
  return stem;
}

export function factoryQuestionStemPreview(
  text: string,
  options?: {
    /** When structured sub-questions exist, strip every trailing (n)… block from main. */
    subQuestions?: string[];
  },
): string {
  const raw = String(text || '').trim();
  const rawCollapsed = raw.replace(/\s+/g, ' ').trim();
  const subs = (options?.subQuestions || [])
    .map((s) => String(s || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  if (!rawCollapsed) {
    return pickEquationSub(subs) || '';
  }

  let t = pickStemLead(raw) || rawCollapsed;

  // Drop exact duplicate of a known sub glued onto the stem.
  for (const sub of subs) {
    const escaped = sub.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    t = t
      .replace(new RegExp(`[（(]\\s*\\d+\\s*[）)]\\s*${escaped}\\s*$`, 'u'), '')
      .replace(new RegExp(`\\s+${escaped}\\s*$`, 'u'), '')
      .trim();
  }

  // Cut before solution / instruction-style (1)(2) blocks.
  const narr = t.search(SOLUTION_NARRATION_RE);
  if (narr > 0) {
    t = t.slice(0, narr).replace(/\s+$/, '');
  }

  // After `=` or closing `$…$`, drop glued (1)… tails (common OCR/extract bleed).
  t = t
    .replace(/([＝=])\$?\s*[（(]\s*\d+\s*[）)][\s\S]*$/u, '$1')
    .replace(/(\$)\s*[（(]\s*\d+\s*[）)][\s\S]*$/u, '$1')
    .trim();

  t = stripTrailingNumberedBlocks(t, subs.length > 0);

  // Main itself may be pure method prose (extract bleed) — treat as thin for fallback.
  if (looksLikeSolutionNarration(t) && !looksLikeEquationStem(t)) {
    const idx = indexPrefix(t) || indexPrefix(rawCollapsed);
    t = idx || t;
  }

  return withEquationFallback(t, subs, rawCollapsed);
}

/**
 * Prefer admission snapshot; if hollow, fall back to structural-unit extract.
 * No stem clipping — admission display should match extract fidelity.
 */
export function fullFactoryParticleQuestionText(input: {
  particleQuestions?: string | null;
  particleTitle?: string | null;
  subQuestions?: string[] | null;
  extractQuestion?: string | null;
  extractSubQuestions?: string[] | null;
  /** Append numbered subs when they are not already embedded in main. */
  includeSubsInline?: boolean;
}): string {
  const snapMain = String(input.particleQuestions || '').trim();
  const extractRaw = String(input.extractQuestion || '').trim();
  const extractMain = extractRaw && extractRaw !== '（无题干）' ? extractRaw : '';
  const title = String(input.particleTitle || '').trim();
  const main = (snapMain && snapMain !== '（无题干）' ? snapMain : extractMain || title) || '';

  const snapSubs = (input.subQuestions || [])
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  const extractSubs = (input.extractSubQuestions || [])
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  const subs = snapSubs.length ? snapSubs : extractSubs;

  if (!subs.length) return main;

  if (!input.includeSubsInline) {
    return main || subs.map((s, i) => `（${i + 1}）${s}`).join('\n');
  }

  const collapsed = main.replace(/\s+/g, ' ');
  const missing = subs.filter((s) => {
    const needle = s.replace(/\s+/g, ' ').trim();
    return Boolean(needle) && !collapsed.includes(needle);
  });
  if (!main) {
    return missing.map((s, i) => `（${i + 1}）${s}`).join('\n');
  }
  if (!missing.length) return main;
  const block = missing
    .map((s) => {
      const ordinal = subs.indexOf(s);
      return `（${(ordinal >= 0 ? ordinal : 0) + 1}）${s}`;
    })
    .join('\n');
  return `${main}\n${block}`;
}

/** @deprecated Use fullFactoryParticleQuestionText for admission list/detail. */
export function resolveFactoryQuestionStem(input: {
  particleQuestions?: string | null;
  particleTitle?: string | null;
  subQuestions?: string[] | null;
  extractQuestion?: string | null;
  extractSubQuestions?: string[] | null;
}): string {
  return fullFactoryParticleQuestionText({ ...input, includeSubsInline: true });
}
